import React from 'react';
import { Form, Input, Typography, Select, Switch } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
// Utils
import { PromoType } from 'helpers/customCampaignConstants';
import { getExpectedApprovals } from 'helpers/customCampaign';
// Components
import ProductsList from './components/ProductsList/ProductsList';
// Style
import styles from './PaymentSection.module.scss';

const { Text } = Typography;
const { Option } = Select;

type PaymentSectionProps = {
  showReservedBudget?: boolean;
  pricePerAthleteLabel?: string;
  pricePerAthleteLocked?: boolean;
};

const PaymentSection = ({
  showReservedBudget = true,
  pricePerAthleteLabel = 'Price Per Talent',
  pricePerAthleteLocked = false,
}: PaymentSectionProps): JSX.Element => {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Payment</h3>
      <div className={styles.subSection}>
        <div className={styles.row}>
          <Form.Item
            name="flatFeePerStore"
            label={<Text>{pricePerAthleteLabel}</Text>}
            extra={
              pricePerAthleteLocked ? (
                <Text type="secondary" className={styles.fieldHint}>
                  The agency has already paid for this offer amount, so it
                  cannot be changed in the current status. It is only editable
                  while the campaign is in draft status.
                </Text>
              ) : undefined
            }
          >
            <Input
              type="number"
              placeholder={`Enter ${pricePerAthleteLabel.toLowerCase()}`}
              aria-label={pricePerAthleteLabel}
              disabled={pricePerAthleteLocked}
            />
          </Form.Item>

          {showReservedBudget && (
            <Form.Item
              name="budget"
              label={<Text>Budget</Text>}
              extra={
                <Text type="secondary" className={styles.fieldHint}>
                  Set your expected budget you’d like to spend on this campaign.
                </Text>
              }
            >
              <Input
                type="number"
                placeholder="Enter budget"
                aria-label="Budget"
              />
            </Form.Item>
          )}
        </div>

        {showReservedBudget && (
          <>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) =>
                prev.budget !== cur.budget ||
                prev.flatFeePerStore !== cur.flatFeePerStore
              }
            >
              {({ getFieldValue }) => {
                const expectedApprovals = getExpectedApprovals(
                  getFieldValue('budget'),
                  getFieldValue('flatFeePerStore')
                );
                return (
                  <div className={styles.expectedApprovals}>
                    <TeamOutlined className={styles.expectedApprovalsIcon} />
                    <div>
                      <Text strong className={styles.expectedApprovalsValue}>
                        {expectedApprovals}{' '}
                        {expectedApprovals === 1 ? 'Talent' : 'Talents'}
                      </Text>
                      <Text type="secondary" className={styles.fieldHint}>
                        Expected Talent Approvals
                      </Text>
                    </div>
                  </div>
                );
              }}
            </Form.Item>

            <div className={styles.reserveBudget}>
              <div>
                <Text strong>Reserve budget upfront</Text>
                <Text type="secondary" className={styles.fieldHint}>
                  Set aside the full amount from the balance at launch, so the
                  campaign never pauses for low funds.
                </Text>
              </div>
              <Form.Item name="reserveBudget" valuePropName="checked" noStyle>
                <Switch aria-label="Reserve budget upfront" />
              </Form.Item>
            </div>
          </>
        )}
      </div>

      <ProductsList />

      <div className={styles.row}>
        <Form.Item
          name="promoType"
          label={<Text>Affiliate Type</Text>}
          style={{ maxWidth: 240 }}
        >
          <Select placeholder="Select type">
            <Option value={PromoType.Code}>Promo Code</Option>
            <Option value={PromoType.Link}>Promo Link</Option>
          </Select>
        </Form.Item>

        <Form.Item
          shouldUpdate={(prev, cur) => prev.promoType !== cur.promoType}
          noStyle
        >
          {({ getFieldValue }) => {
            const promoType = getFieldValue('promoType');
            return (
              <Form.Item
                name="promoValue"
                label={
                  <Text>
                    {promoType === PromoType.Link
                      ? 'Promo Link (Optional)'
                      : 'Promo Code (Optional)'}
                  </Text>
                }
              >
                <Input
                  type="text"
                  placeholder={
                    promoType === PromoType.Link
                      ? 'https://brand.com/offer'
                      : 'ADIDAS2026'
                  }
                  aria-label={
                    promoType === PromoType.Link
                      ? 'Promo link for talents to use'
                      : 'Promo code for talents to use'
                  }
                />
              </Form.Item>
            );
          }}
        </Form.Item>
      </div>
    </section>
  );
};

export default PaymentSection;
